import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import PanoViewer from './PanoViewer';
import PanoEvents from '../Enumerations/ViewerEnums';
import PanoConstants from '../constants/ViewerConstants';
import PanoDome from './PanoDome';
interface IBoxProperties {
    realWorldPos: BABYLON.Vector3;
    xDim: number; // depth  -> world Z
    yDim: number; // height -> world Y
    zDim: number; // width  -> world X
}

interface ISceneBoxProperties extends IBoxProperties {
    scenePos: BABYLON.Vector3;
}

/** Host types BoxManager can attach to */
type BoxHost =
    | { kind: 'pano'; viewer: PanoViewer }
    | { kind: 'scene'; scene: BABYLON.Scene };

/** Minimal adapter for what BoxManager needs */
interface IBoxEnv {
    scene: BABYLON.Scene;
    worldToSceneAnchor(pos: BABYLON.Vector3): { scenePos: BABYLON.Vector3, extra?: any };
    onAttach?(env: IBoxEnv): void;
    onDispose?(): void;
}

class BoxManager {
    public static BOX_NAME = 'annBox';
    private static DefaultBoxColor = '#00ff0040';
    private _selectedBoxMaterial: BABYLON.StandardMaterial | null = null;

    private _host: BoxHost;
    private _env: IBoxEnv;

    // ---------- PANO (single box) ----------
    private _boxMesh: BABYLON.Mesh | null = null;
    private _boxProperties!: IBoxProperties;

    // ---------- SCENE (multi-box) ----------
    private _sceneMeshes = new Map<string, BABYLON.Mesh>();
    private _sceneProps = new Map<string, IBoxProperties>();
    private _selectedIds = new Set<string>();
    private _focusedId: string | null = null;
    private _sceneMode: 'none' | 'move' | 'scale' = 'none';

    private _boxColor: string = BoxManager.DefaultBoxColor;
    private _boxMaterial: BABYLON.StandardMaterial | null = null;
    private _colorizedBoxMaterials = new Map<string, BABYLON.StandardMaterial>();
    private _boxColorization = new Map<string, string>();

    private _attachedGizmo: BABYLON.IGizmo | null = null;
    private _scaleGizmoBB: BABYLON.BoundingBoxGizmo | null = null; // scene-only

    private _activeGizmoDragCount = 0;

    public events: EventTarget = new EventTarget();

    private get scene() { return this._env.scene; }
    private get activeCamera() { return this.scene.activeCamera!; }

    constructor(host: BoxHost) {
        this._host = host;
        this._env = this._createEnv(host);

        this.scene.onPointerObservable.add(this._handlePointerObservable);
        this.scene.onKeyboardObservable.add((evt) => { this._handleKeyboardObservable(evt); });
        this.scene.onBeforeCameraRenderObservable.add(this._handleBeforeCameraRenderObservable);

        this._env.onAttach?.(this._env);
    }

    public dispose() {
        this.clearBox();
        this.scene.onPointerObservable.removeCallback(this._handlePointerObservable);
        this.scene.onBeforeCameraRenderObservable.removeCallback(this._handleBeforeCameraRenderObservable);
        this._env.onDispose?.();
    }

    public clearBox() {
        this._attachedGizmo?.dispose(); this._attachedGizmo = null;
        this._scaleGizmoBB?.dispose(); this._scaleGizmoBB = null;
        this._boxMesh?.dispose(); this._boxMesh = null;

        this._sceneMeshes.forEach((mesh) => {
            mesh.dispose();
        });

        this._sceneMeshes.clear();
        this._sceneProps.clear();
        this._selectedIds.clear();
        this._focusedId = null;
        this._sceneMode = 'none';
        this._boxColorization.clear();

        this._advancedTexture?.dispose(); this._advancedTexture = null;
    }

    public clearEditingGizmos() {
        this._attachedGizmo?.dispose();
        this._attachedGizmo = null;

        this._scaleGizmoBB?.dispose();
        this._scaleGizmoBB = null;

        if (this._activeGizmoDragCount === 0) {
            this._resumeCameraControlsIfIdle();
        }
    }

    // ------------------------------------------------------
    // Public API (shared)
    // ------------------------------------------------------

    public setBoxVisibility(id: string, visible: boolean) {
        const mesh = this._sceneMeshes.get(id);
        if (mesh) mesh.setEnabled(visible);
    }

    /** PANO helper (kept for compatibility) */
    public createBox(boxProps: IBoxProperties) {
        if (this._host.kind !== 'pano') {
            // In scene mode, prefer upsertBox with an id
            return;
        }
        const { scenePos } = this._env.worldToSceneAnchor(boxProps.realWorldPos);
        this._boxMesh = this._createDemoBox({ scenePos, ...boxProps }, this.scene);
        this._boxProperties = { ...boxProps };
    }

    public setBoxColor(color: string) {
        this._boxColor = color;
        this._boxMaterial = null;
        this._selectedBoxMaterial = null;

        this._sceneMeshes.forEach((_m, id) => {
            this._applyMaterialForBox(id);
        });

        if (this._host.kind === 'pano' && this._boxMesh) {
            this._boxMesh.material = this._getBoxMaterial();
        }
    }

    /** get props for focused id by default; pass id to get a specific one in scene mode */
    public getBoxProperties(id?: string) {
        if (this._host.kind === 'pano') return this._boxProperties;
        const key = id ?? this._focusedId ?? undefined;
        return key ? this._sceneProps.get(key) : undefined;
    }

    public setPositioningMode(mode: 'move' | 'scale' | 'none') {
        if (this._host.kind === 'pano') {
            if (mode === 'none') {
                this._attachedGizmo?.dispose(); this._attachedGizmo = null;
                return;
            }
            if (mode === 'move') this._createPositionGizmoPano();
            else this._createScaleGizmoPano();
            return;
        }

        this._sceneMode = mode;
        this._refreshSceneSelectionState();
    }

    public getBoxMesh = (id?: string): BABYLON.Mesh | undefined => {
        // ...your robust version here...
        if (this._host.kind === 'pano') {
            const m = this._boxMesh || undefined;
            if (!m) return undefined;
            const disposed = (m as any).isDisposed ? (m as any).isDisposed() : !!(m as any)._isDisposed;
            return disposed ? undefined : m;
        }

        if (!id) return undefined;

        let m = this._sceneMeshes.get(id);
        if (m) {
            const disposed = (m as any).isDisposed ? (m as any).isDisposed() : !!(m as any)._isDisposed;
            if (!disposed) return m;
            this._sceneMeshes.delete(id);
            m = undefined;
        }

        const fallback = this.scene.getMeshById(id) as BABYLON.Mesh | null;
        if (fallback && fallback.name === BoxManager.BOX_NAME) {
            this._sceneMeshes.set(id, fallback);
            return fallback;
        }

        // optional: metadata fallback
        const byMeta = this.scene.meshes.find(mm =>
            (mm as BABYLON.Mesh).metadata?.boxId === id
        ) as BABYLON.Mesh | undefined;
        if (byMeta) { this._sceneMeshes.set(id, byMeta); return byMeta; }

        return undefined;
    };

    // ------------------------------------------------------
    // Scene multi-box API
    // ------------------------------------------------------

    /** Create or update a box for a given id (scene mode). */
    public upsertBox(id: string, props: IBoxProperties) {
        if (this._host.kind !== 'scene') return;

        const existing = this._sceneMeshes.get(id);
        const mapped = this._env.worldToSceneAnchor(props.realWorldPos);
        if (!existing) {
            const mesh = this._createSceneUnitBox();
            mesh.id = id;
            mesh.metadata = mesh.metadata ?? {};
            mesh.metadata.boxId = id;
            mesh.scaling.set(props.zDim, props.yDim, props.xDim);
            mesh.position.copyFrom(mapped.scenePos);
            this._sceneMeshes.set(id, mesh);
            this._sceneProps.set(id, { ...props });
            this._applyMaterialForBox(id);
            return;
        }

        existing.scaling.set(props.zDim, props.yDim, props.xDim);
        existing.position.copyFrom(mapped.scenePos);
        this._sceneProps.set(id, { ...props });
        this._applyMaterialForBox(id);
    }

    /** Remove a specific scene box by id. */
    public removeBox(id: string) {
        if (this._host.kind !== 'scene') return;
        const m = this._sceneMeshes.get(id);
        if (m) m.dispose();
        this._sceneMeshes.delete(id);
        this._sceneProps.delete(id);
        this._boxColorization.delete(id);
        this._selectedIds.delete(id);
        if (this._focusedId === id) {
            this._focusedId = null;
        }
        this._refreshSceneSelectionState();
    }

    public setSelectedBox(id: string | null, isSelected?: boolean) {
        if (this._host.kind !== 'scene') return;

        if (typeof isSelected === 'undefined') {
            this._selectedIds.clear();
            if (id) {
                this._selectedIds.add(id);
            }
        } else {
            if (id == null) {
                if (isSelected === false) {
                    this._selectedIds.clear();
                }
            } else if (isSelected) {
                this._selectedIds.add(id);
            } else {
                this._selectedIds.delete(id);
            }
        }

        this._refreshSceneSelectionState();
    }

    public getSelectedBoxIds(): string[] {
        return Array.from(this._selectedIds);
    }

    private _refreshSceneSelectionState() {
        if (this._host.kind !== 'scene') return;

        this._attachedGizmo?.dispose(); this._attachedGizmo = null;
        this._scaleGizmoBB?.dispose(); this._scaleGizmoBB = null;

        this._focusedId = this._selectedIds.size === 1 ? Array.from(this._selectedIds)[0] : null;

        this._sceneMeshes.forEach((_mesh, id) => {
            this._applyMaterialForBox(id);
        });

        if (this._focusedId) {
            const focused = this._sceneMeshes.get(this._focusedId);
            if (focused) {
                focused.renderingGroupId = 1;
            }
        }

        if (!this._focusedId || this._sceneMode === 'none') {
            this._sceneMeshes.forEach((mesh) => {
                mesh.renderingGroupId = 0;
            });
            if (this._activeGizmoDragCount === 0) {
                this._resumeCameraControlsIfIdle();
            }
            return;
        }

        if (this._sceneMode === 'move') this._createPositionGizmoScene();
        else this._createScaleGizmoScene();
    }

    private _applyMaterialForBox(id: string) {
        const mesh = this._sceneMeshes.get(id);
        if (!mesh) return;

        if (this._selectedIds.has(id)) {
            mesh.material = this._getSelectedBoxMaterial();
            return;
        }

        const colorized = this._boxColorization.get(id);
        if (colorized) {
            mesh.material = this._getColorizedBoxMaterial(colorized);
            return;
        }

        mesh.material = this._getBoxMaterial();
    }

    private _getSelectedBoxMaterial() {
        const base = this._getBoxMaterial();

        // derive selected alpha from base (e.g., 2× opaque, max 1.0)
        const selectedAlpha = Math.min(1, base.alpha * 3);

        if (!this._selectedBoxMaterial) {
            const mat = new BABYLON.StandardMaterial('boxMatSelected', this.scene);
            mat.alpha = selectedAlpha;
            // keep same “feel” as base but with different hue
            mat.diffuseColor = BABYLON.Color3.FromHexString('#00c6cd');
            mat.specularColor = base.specularColor.clone();
            mat.emissiveColor = base.emissiveColor.clone();
            mat.ambientColor = BABYLON.Color3.FromHexString('#00c6cd');
            this._selectedBoxMaterial = mat;
        } else {
            // keep alpha & other feel in sync with base
            this._selectedBoxMaterial.alpha = selectedAlpha;
            this._selectedBoxMaterial.specularColor = base.specularColor.clone();
            this._selectedBoxMaterial.emissiveColor = base.emissiveColor.clone();
        }
        return this._selectedBoxMaterial;
    }

    private _getColorizedBoxMaterial(colorHex: string) {
        const base = this._getBoxMaterial();

        let mat = this._colorizedBoxMaterials.get(colorHex);
        if (!mat) {
            mat = new BABYLON.StandardMaterial(`boxMat${colorHex}`, this.scene);
            // keep same alpha & feel as base

            var color = BABYLON.Color4.FromHexString(colorHex);
            mat.alpha = color.a;
            mat.diffuseColor = new BABYLON.Color3(color.r, color.g, color.b);
            mat.specularColor = base.specularColor.clone();
            mat.emissiveColor = base.emissiveColor.clone();
            mat.ambientColor = new BABYLON.Color3(color.r, color.g, color.b);
            this._colorizedBoxMaterials.set(colorHex, mat);
        }
        
        return mat;
    }

    public colorizeBox(id: string, colorHex: string) {
        if (this._host.kind !== 'scene') return;
        this._boxColorization.set(id, colorHex);
        this._applyMaterialForBox(id);
    }

    public resetBoxColorization(id: string) {
        if (this._host.kind !== 'scene') return;
        this._boxColorization.delete(id);
        this._applyMaterialForBox(id);
    }

    // ------------------------------------------------------
    // Input handlers
    // ------------------------------------------------------

    private _handleKeyboardObservable = (evt: BABYLON.KeyboardInfo) => {
        switch (evt.type) {
            case BABYLON.KeyboardEventTypes.KEYDOWN:
                if (evt.event.key === 'g') this.setPositioningMode('move');
                else if (evt.event.key === 'h') this.setPositioningMode('scale');
                break;
        }
    };

    private _handleBeforeCameraRenderObservable = (_camera: BABYLON.Camera) => { /* no-op */ }

    private _handlePointerObservable = (evt: BABYLON.PointerInfo) => {
        if (evt.event.button === 2 && this._attachedGizmo) {
            let released = false;
            const g: any = this._attachedGizmo as any;
            if (g.xGizmo && g.xGizmo.dragBehavior?.dragging) { g.releaseDrag(); released = true; }
            if (g.yGizmo && g.yGizmo.dragBehavior?.dragging) { g.releaseDrag(); released = true; }
            if (g.zGizmo && g.zGizmo.dragBehavior?.dragging) { g.releaseDrag(); released = true; }
            if (g.uniformScaleGizmo && g.uniformScaleGizmo.dragBehavior?.dragging) { g.releaseDrag(); released = true; }
            if (released) {
                this._attachedGizmo?.dispose(); this._attachedGizmo = null;
                if (g.uniformScaleGizmo) this.setPositioningMode('scale');
                else this.setPositioningMode('move');
            }
        }
    };

    // ------------------------------------------------------
    // Move/Scale helpers (PAN0)
    // ------------------------------------------------------

    private _moveDemoBox(distance: number, axis: BABYLON.Vector3) {
        if (!distance) return;
        this._boxProperties.realWorldPos.addInPlace(axis.scale(distance));
        this._createDemoBoxAtWorldPosition(this._boxProperties);
    }

    /** Multiply-style scaler (matches your original pano behavior). */
    private _scaleDemoBox(scale: number, axis: BABYLON.Vector3) {
        if (scale <= 0) return;

        if (axis.x) this._boxProperties.xDim *= scale;
        if (axis.y) this._boxProperties.yDim *= scale;
        if (axis.z) this._boxProperties.zDim *= scale;

        this._createDemoBoxAtWorldPosition(this._boxProperties);
    }

    private _createDemoBoxAtWorldPosition(boxProps: IBoxProperties) {
        const info = (this._host as any).viewer.worldCoordinateInformation(boxProps.realWorldPos);
        const boxPoint = info.imageCoordinate;
        const distance = info.distance;

        const d2 = boxPoint.subtract(this.scene.activeCamera.position).normalize();
        const scenePos = this.scene.activeCamera.position.add(d2.scale(distance));

        this._boxMesh = this._createDemoBox({ scenePos: scenePos, ...boxProps }, this.scene);
        this._boxProperties = boxProps;
    }

    // ------------------------------------------------------
    // Geometry build / projection
    // ------------------------------------------------------

    private _createDemoBox(boxProps: ISceneBoxProperties, scene: BABYLON.Scene) {
        if (this._host.kind === 'pano') {
            // Project a param box to the dome by radial projection from the camera.
            const box = BABYLON.MeshBuilder.CreateBox(
                'tempBox',
                { width: boxProps.zDim, height: boxProps.yDim, depth: boxProps.xDim },
                scene
            );
            const vertices = box.getVerticesData(BABYLON.VertexBuffer.PositionKind)!;
            const normals = box.getVerticesData(BABYLON.VertexBuffer.NormalKind)!;
            const indices = box.geometry!.getIndices()!;

            const projectedVertices: number[] = [];
            let projectedCenter = new BABYLON.Vector3(0, 0, 0);

            for (let i = 0; i < vertices.length; i += 3) {
                const vp = new BABYLON.Vector3(
                    vertices[i] + boxProps.scenePos.x,
                    vertices[i + 1] + boxProps.scenePos.y,
                    vertices[i + 2] + boxProps.scenePos.z
                );
                const dir = vp.subtract(this.activeCamera.position).normalize();
                const proj = this.activeCamera.position.add(dir.scale(PanoDome.DomeRadius));
                projectedVertices.push(proj.x, proj.y, proj.z);
                projectedCenter.addInPlace(proj);
            }
            projectedCenter.scaleInPlace(1 / (vertices.length / 3));

            // Center them around projectedCenter
            for (let i = 0; i < projectedVertices.length; i += 3) {
                projectedVertices[i] -= projectedCenter.x;
                projectedVertices[i + 1] -= projectedCenter.y;
                projectedVertices[i + 2] -= projectedCenter.z;
            }

            if (!this._boxMesh) {
                this._boxMesh = new BABYLON.Mesh(BoxManager.BOX_NAME, scene);
                this._boxMesh.renderingGroupId = PanoConstants.RenderingGroups.ObjectsRenderingGroupId;
                this._boxMesh.material = this._getBoxMaterial();
            }

            const projectedBox = this._boxMesh;
            projectedBox.position = projectedCenter;
            projectedBox.setVerticesData(BABYLON.VertexBuffer.PositionKind, projectedVertices);
            projectedBox.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals);
            projectedBox.setIndices(indices);

            box.dispose();
            return projectedBox;
        } else {
            // Scene mode: single unit box driven by scaling & position (used only by pano path above).
            return this._createSceneUnitBox();
        }
    }

    private _createSceneUnitBox() {
        const mesh = BABYLON.MeshBuilder.CreateBox(
            BoxManager.BOX_NAME,
            { width: 1, height: 1, depth: 1 },
            this.scene
        );
        mesh.material = this._getBoxMaterial();
        return mesh;
    }

    private _getBoxMaterial() {
        if (!this._boxMaterial) {
            const mat = new BABYLON.StandardMaterial('boxMat', this.scene);
            const c = BABYLON.Color4.FromHexString(this._boxColor);
            mat.alpha = c.a;
            mat.diffuseColor = new BABYLON.Color3(c.r, c.g, c.b);
            mat.specularColor = new BABYLON.Color3(1, 1, 1);
            mat.emissiveColor = new BABYLON.Color3(0, 0, 0);
            mat.ambientColor = new BABYLON.Color3(0, 1, 0);
            this._boxMaterial = mat;
        }
        return this._boxMaterial;
    }

    // ------------------------------------------------------
    // Gizmos
    // ------------------------------------------------------

    // ----- PANO (old, working gizmo flow) -----
    private _createPositionGizmoPano() {
        this._attachedGizmo?.dispose();
        const gizmo = new BABYLON.PositionGizmo();
        gizmo.attachedMesh = this._boxMesh!;
        this._setupCustomPositionRelease(gizmo);

        const dragConstant = 0.010;
        gizmo.xGizmo.dragBehavior.onDragObservable.clear();
        gizmo.yGizmo.dragBehavior.onDragObservable.clear();
        gizmo.zGizmo.dragBehavior.onDragObservable.clear();

        gizmo.xGizmo.dragBehavior.onDragObservable.add((e) => {
            this._moveDemoBox(e.dragDistance * dragConstant, BABYLON.Vector3.Forward());
        });
        gizmo.yGizmo.dragBehavior.onDragObservable.add((e) => {
            this._moveDemoBox(e.dragDistance * dragConstant, BABYLON.Vector3.Up());
        });
        gizmo.zGizmo.dragBehavior.onDragObservable.add((e) => {
            this._moveDemoBox(e.dragDistance * dragConstant, BABYLON.Vector3.Left());
        });

        gizmo.onDragEndObservable.add(() => {
            this._dispatchChanged(); // pano single
        });

        this._attachedGizmo = gizmo;
    }

    private _createScaleGizmoPano() {
        this._attachedGizmo?.dispose();

        const gizmo = new BABYLON.ScaleGizmo();
        this._setupCustomScaleRelease(gizmo);
        this._attachGizmoPano(gizmo);

        const scaleConstant = 0.010;

        gizmo.xGizmo.dragBehavior.onDragObservable.clear();
        gizmo.yGizmo.dragBehavior.onDragObservable.clear();
        gizmo.zGizmo.dragBehavior.onDragObservable.clear();

        gizmo.xGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, BABYLON.Vector3.Forward());
        });
        gizmo.yGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, BABYLON.Vector3.Up());
        });
        gizmo.zGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, BABYLON.Vector3.Left());
        });
        gizmo.uniformScaleGizmo.dragBehavior.onDragObservable.add((event) => {
            this._scaleDemoBox(1 + event.dragDistance * scaleConstant, new BABYLON.Vector3(1, 1, 1));
        });

        gizmo.onDragEndObservable.add(() => {
            this._dispatchChanged(); // pano single
        });

        this._attachedGizmo = gizmo;
    }

    private _attachGizmoPano(gizmo: BABYLON.IGizmo) {
        this._attachedGizmo?.dispose();
        gizmo.attachedMesh = this._boxMesh!;
        this._attachedGizmo = gizmo;
    }

    // ----- SCENE (BoundingBoxGizmo & PositionGizmo on selected only) -----
    private _createPositionGizmoScene() {
        if (!this._focusedId) return;
        const mesh = this._sceneMeshes.get(this._focusedId);
        if (!mesh) return;

        this._attachedGizmo?.dispose();
        const gizmo = new BABYLON.PositionGizmo();
        gizmo.attachedMesh = mesh;

        const start = () => { if (this._activeGizmoDragCount++ === 0) this._suspendCameraControls(); };
        const end = () => {
            if (!this._focusedId) return;
            const props = this._sceneProps.get(this._focusedId);
            if (props) {
                props.realWorldPos = mesh.position.clone();
                this._sceneProps.set(this._focusedId, { ...props });
            }

            this._activeGizmoDragCount = Math.max(0, this._activeGizmoDragCount - 1);
            this._resumeCameraControlsIfIdle();
            this._dispatchChanged(this._focusedId);
        };

        gizmo.xGizmo.dragBehavior.onDragStartObservable.add(start);
        gizmo.yGizmo.dragBehavior.onDragStartObservable.add(start);
        gizmo.zGizmo.dragBehavior.onDragStartObservable.add(start);

        gizmo.xGizmo.dragBehavior.onDragEndObservable.add(end);
        gizmo.yGizmo.dragBehavior.onDragEndObservable.add(end);
        gizmo.zGizmo.dragBehavior.onDragEndObservable.add(end);

        this._attachedGizmo = gizmo;
    }

    private _elevateBoundingBoxGizmoRendering3(gizmo: BABYLON.BoundingBoxGizmo, groupId = 2) {
        const root = (gizmo as any)._rootMesh as BABYLON.AbstractMesh | undefined;
        if (!root) return;

        const meshes = root.getChildMeshes(false);
        meshes.push(root);

        for (const m of meshes) {
            // Draw after normal geometry
            m.renderingGroupId = groupId;

            // ✅ Keep picking active
            m.isPickable = true;
            m.alwaysSelectAsActiveMesh = true;

            // ✅ Do NOT disable depth test/write
            const mat = m.material as BABYLON.Material | null;
            if (mat) {
                // Just bias the depth a tiny bit so it renders visually “on top”
                try { (mat as any).zOffset = -2; } catch { }
            }
        }
    }

    // Works but picking and sliding doesn't work!
    private _elevateBoundingBoxGizmoRendering(gizmo: BABYLON.BoundingBoxGizmo, groupId = 2) {
        const root = (gizmo as any)._rootMesh as BABYLON.AbstractMesh | undefined;
        if (!root) return;

        // include root + its children (lines/corners live under here)
        const meshes = root.getChildMeshes(false);
        meshes.push(root);

        for (const m of meshes) {
            // draw late
            m.renderingGroupId = groupId;
            // keep it interactive even if far
            m.alwaysSelectAsActiveMesh = true;

            const mat = m.material as BABYLON.Material | null;
            if (mat) {
                // don’t write to depth
                mat.disableDepthWrite = true;

                // try to bypass depth test entirely (Babylon >= 5)
                try { (mat as any).depthFunction = BABYLON.Constants.ALWAYS; } catch { }

                // helpful fallback on older builds: nudge toward the camera
                try { (mat as any).zOffset = -10; } catch { }
            }
        }
    }

    private _createScaleGizmoScene() {
        if (!this._focusedId) return;
        const mesh = this._sceneMeshes.get(this._focusedId);
        if (!mesh) return;

        this._scaleGizmoBB?.dispose();
        const gizmo = new BABYLON.BoundingBoxGizmo(BABYLON.Color3.FromHexString('#0984e3'));
        gizmo.setEnabledRotationAxis('');
        gizmo.scaleDragSpeed = 1.5;
        gizmo.attachedMesh = mesh;
        
        const startDrag = () => {
            if (this._activeGizmoDragCount++ === 0) this._suspendCameraControls();
        };
        const endDrag = () => {
            if (!this._focusedId) return;
            const s = mesh.scaling;
            const props = this._sceneProps.get(this._focusedId);
            if (props) {
                props.zDim = s.x;
                props.yDim = s.y;
                props.xDim = s.z;
                props.realWorldPos = mesh.position.clone();
                this._sceneProps.set(this._focusedId, { ...props });
            }

            this._activeGizmoDragCount = Math.max(0, this._activeGizmoDragCount - 1);
            this._resumeCameraControlsIfIdle();
            this._dispatchChanged(this._focusedId);
        };

        const gAny = gizmo as any;
        if (gAny.onScaleBoxDragStartObservable) gAny.onScaleBoxDragStartObservable.add(startDrag);

        let started = false;
        gizmo.onScaleBoxDragObservable.add(() => {
            if (!started) { started = true; startDrag(); }
            // live clamp
            const s = mesh.scaling;
            s.x = Math.max(0.001, s.x);
            s.y = Math.max(0.001, s.y);
            s.z = Math.max(0.001, s.z);
        });
        gizmo.onScaleBoxDragEndObservable.add(() => {
            started = false;
            endDrag();
        });



        //gizmo.gizmoLayer = BABYLON.UtilityLayerRenderer.DefaultUtilityLayer;

        // Make sure that layer actually clears depth/stencil each frame
        //gizmo.gizmoLayer.utilityLayerScene.autoClearDepthAndStencil = true;

        // (optional but nice) mirror handedness if your scene uses RHS
        //gizmo.gizmoLayer.utilityLayerScene.useRightHandedSystem = scene.useRightHandedSystem;

        this._elevateBoundingBoxGizmoRendering(gizmo, 1);
        this._scaleGizmoBB = gizmo;
        this._attachedGizmo = gizmo;
    }

    // ------------------------------------------------------
    // Custom release handlers (pano)
    // ------------------------------------------------------

    /** Replaces releaseDrag on ScaleGizmo’s axis behaviors to preserve pano input system. */
    private _setupCustomScaleRelease(gizmo: BABYLON.ScaleGizmo) {
        const patch = (db: any) => {
            db.releaseDrag = () => {
                const that = db;
                if (that.dragging) {
                    that.dragging = false;
                    that.onDragEndObservable.notifyObservers({
                        dragPlanePoint: that.lastDragPosition,
                        pointerId: that.currentDraggingPointerId,
                        pointerInfo: that._activePointerInfo
                    });
                }
                that.currentDraggingPointerId = -1;
                that._activeDragButton = -1;
                that._activePointerInfo = null;
                that._moving = false;

                // Reattach camera controls (your custom logic)
                if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                    if (that._scene.activeCamera.getClassName() === 'ArcRotateCamera') {
                        const arc = that._scene.activeCamera as BABYLON.ArcRotateCamera;
                        arc.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                        BABYLON.Scene.DoubleClickDelay = 500;
                        (arc as any).inputs.attached.pointers.buttons = [0, 1] as any;
                        arc.panningSensibility = 0;
                        arc.inputs.attached.mousewheel.detachControl();
                        (arc.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                        (arc.inputs.attached.pointers as any).multiTouchPanning = false;
                        arc.pinchToPanMaxDistance = 1e10;
                    } else {
                        that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                    }
                    that._attachedToElement = false;
                }
            };
        };

        patch((gizmo.xGizmo as any).dragBehavior);
        patch((gizmo.yGizmo as any).dragBehavior);
        patch((gizmo.zGizmo as any).dragBehavior);
        patch((gizmo.uniformScaleGizmo as any).dragBehavior);
    }

    /** Replaces releaseDrag on PositionGizmo’s axis behaviors to preserve pano input system. */
    private _setupCustomPositionRelease(gizmo: BABYLON.PositionGizmo) {
        const patch = (db: any) => {
            db.releaseDrag = () => {
                const that = db;
                if (that.dragging) {
                    that.dragging = false;
                    that.onDragEndObservable.notifyObservers({
                        dragPlanePoint: that.lastDragPosition,
                        pointerId: that.currentDraggingPointerId,
                        pointerInfo: that._activePointerInfo
                    });
                }
                that.currentDraggingPointerId = -1;
                that._activeDragButton = -1;
                that._activePointerInfo = null;
                that._moving = false;

                if (that.detachCameraControls && that._attachedToElement && that._scene.activeCamera && !that._scene.activeCamera.leftCamera) {
                    if (that._scene.activeCamera.getClassName() === 'ArcRotateCamera') {
                        const arc = that._scene.activeCamera as BABYLON.ArcRotateCamera;
                        arc.attachControl(that._scene.getEngine().getRenderingCanvas(), true);
                        BABYLON.Scene.DoubleClickDelay = 500;
                        (arc as any).inputs.attached.pointers.buttons = [0, 1] as any;
                        arc.panningSensibility = 0;
                        arc.inputs.attached.mousewheel.detachControl();
                        (arc.inputs.attached.pointers as any).multiTouchPanAndZoom = false;
                        (arc.inputs.attached.pointers as any).multiTouchPanning = false;
                        arc.pinchToPanMaxDistance = 1e10;
                    } else {
                        that._scene.activeCamera.attachControl(that._scene.activeCamera.inputs ? that._scene.activeCamera.inputs.noPreventDefault : true);
                    }
                    that._attachedToElement = false;
                }
            };
        };

        patch((gizmo.xGizmo as any).dragBehavior);
        patch((gizmo.yGizmo as any).dragBehavior);
        patch((gizmo.zGizmo as any).dragBehavior);
    }

    // ------------------------------------------------------
    // Camera control pause/resume (scene gizmos)
    // ------------------------------------------------------

    private _suspendCameraControls() {
        const cam = this.scene.activeCamera as any;
        if (cam && typeof cam.setControlsSuspended === 'function') cam.setControlsSuspended(true);
    }
    private _resumeCameraControlsIfIdle() {
        if (this._activeGizmoDragCount === 0) {
            const cam = this.scene.activeCamera as any;
            if (cam && typeof cam.setControlsSuspended === 'function') cam.setControlsSuspended(false);
        }
    }

    // ------------------------------------------------------
    // Debug overlay (optional)
    // ------------------------------------------------------

    private _advancedTexture: BABYLONGUI.AdvancedDynamicTexture | null = null;
    private _debugTextBlock: BABYLONGUI.TextBlock | null = null;

    private _updateDemoBoxDebugText(): void {
        const b = this._host.kind === 'pano' ? this._boxProperties : (this._focusedId ? this._sceneProps.get(this._focusedId)! : undefined);
        if (!b) return;
        const t = `X: ${b.realWorldPos.x.toFixed(3)} m
Y: ${b.realWorldPos.y.toFixed(3)} m
Z: ${b.realWorldPos.z.toFixed(3)} m
X-dim: ${b.xDim.toFixed(3)} m
Y-dim: ${b.yDim.toFixed(3)} m
Z-dim: ${b.zDim.toFixed(3)} m`;
        this._createDebugTextBox(this.scene, t);
    }

    private _createDebugTextBox(scene: BABYLON.Scene, initialText: string): void {
        if (!this._advancedTexture) {
            this._advancedTexture = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI('UI', true, scene);
            const rect = new BABYLONGUI.Rectangle();
            rect.width = '180px';
            rect.height = '150px';
            rect.cornerRadius = 20;
            rect.color = 'White';
            rect.thickness = 2;
            rect.background = 'Black';
            rect.alpha = 0.5;
            rect.verticalAlignment = BABYLONGUI.Control.VERTICAL_ALIGNMENT_BOTTOM;
            rect.horizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
            rect.leftInPixels = -150;
            this._advancedTexture.addControl(rect);

            this._debugTextBlock = new BABYLONGUI.TextBlock();
            this._debugTextBlock.color = 'white';
            this._debugTextBlock.textWrapping = true;
            this._debugTextBlock.textHorizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
            this._debugTextBlock.paddingLeft = '10px';
            rect.addControl(this._debugTextBlock);
        }
        if (this._debugTextBlock) this._debugTextBlock.text = initialText;
    }

    // ------------------------------------------------------
    // Events
    // ------------------------------------------------------
    private _dispatchChanged(id?: string) {
        if (id) {
            this.events?.dispatchEvent(new CustomEvent(PanoEvents.BoxManager.BOX_CHANGED, { detail: { id } }));
        } else {
            this.events?.dispatchEvent(new Event(PanoEvents.BoxManager.BOX_CHANGED));
        }
    }

    // ------------------------------------------------------
    // ENV FACTORY
    // ------------------------------------------------------

    private _createEnv(host: BoxHost): IBoxEnv {
        if (host.kind === 'pano') {
            const viewer = host.viewer;
            return {
                scene: viewer.scene,
                worldToSceneAnchor: (realWorldPos: BABYLON.Vector3) => {
                    const info = viewer.worldCoordinateInformation(realWorldPos);
                    const boxPoint = info.imageCoordinate;
                    const distance = info.distance;

                    const d2 = boxPoint.subtract(this.scene.activeCamera!.position).normalize();
                    const scenePos = this.scene.activeCamera!.position.add(d2.scale(distance));
                    return { scenePos, extra: info };
                }
            };
        } else {
            const scene = host.scene;
            return {
                scene,
                worldToSceneAnchor: (realWorldPos: BABYLON.Vector3) => {
                    const scenePos = realWorldPos.clone();
                    return { scenePos };
                }
            };
        }
    }
}

export default BoxManager;